import { useRef, useState } from "react";
import classNames from "classnames";
import { Icons } from "assets";
import ProjectNotificationBanner from "../projectNotificationBanner";
import styles from "./styles.module.scss";

export interface BannerItem {
  key: string | number;
  title: string;
  description?: string;
  buttonText?: string;
  showButton?: boolean;
  onButtonClick?: () => void;
  // Optional second action ("View Project") that navigates without
  // dismissing the card.
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  // Called once when the card is swiped away so the caller can persist the
  // dismissal (e.g. mark the revision viewed) — the card must not come back
  // on the next fetch.
  onDismiss?: () => void;
}

interface ProjectNotificationBannerCarouselProps {
  items: BannerItem[];
}

// Horizontal drag distance (px) that counts as a swipe-browse.
const SWIPE_THRESHOLD = 90;
// Movement (px) before a pointer-down is treated as a drag instead of a click.
const DRAG_START_TOLERANCE = 6;
// How many cards are visible in the stack (top card + peeking edges).
const VISIBLE_STACK_DEPTH = 3;
// Must match the CSS fly-out transition duration.
const FLY_OUT_MS = 260;

/**
 * Stacked-card deck of project notification pop-ups. The top card can be
 * swiped left or right (a flat horizontal slide) to browse to the next card;
 * swiping only rotates the deck and never consumes a card. The only way to
 * permanently dismiss a card (firing the item's onDismiss so it stays gone
 * per user) is the ✕ button. Arrow buttons also browse without dismissing.
 */
const ProjectNotificationBannerCarousel = ({
  items,
}: ProjectNotificationBannerCarouselProps) => {
  const [dismissedKeys, setDismissedKeys] = useState<Set<string | number>>(
    () => new Set(),
  );
  const [rotation, setRotation] = useState(0);
  const [flyingKey, setFlyingKey] = useState<string | number | null>(null);
  const [flyDirection, setFlyDirection] = useState<1 | -1>(1);
  const [flyMode, setFlyMode] = useState<"dismiss" | "browse">("dismiss");
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const pointerIdRef = useRef<number | null>(null);
  const draggedRef = useRef(false);

  const visibleItems = items.filter((item) => !dismissedKeys.has(item.key));

  if (visibleItems.length === 0) return null;

  // The deck is rotated (not sliced) so browsing never loses cards; the
  // rotation is re-normalized against the current count so dismissals keep
  // it in range.
  const count = visibleItems.length;
  const safeRotation = ((rotation % count) + count) % count;
  const orderedItems = [
    ...visibleItems.slice(safeRotation),
    ...visibleItems.slice(0, safeRotation),
  ];

  const topItem = orderedItems[0];
  const isFlying = flyingKey === topItem.key;

  const finishDismiss = (item: BannerItem) => {
    setDismissedKeys((prev) => {
      const next = new Set(prev);
      next.add(item.key);
      return next;
    });
    setFlyingKey(null);
    setDragX(0);
    item.onDismiss?.();
  };

  // Browsing slides the top card away and rotates the deck by `step`: the card
  // is NOT consumed and onDismiss never fires, so it comes back as you cycle
  // through. Only the ✕ button (dismiss mode) removes a card for good.
  const finishBrowse = (step: 1 | -1) => {
    setRotation((r) => r + step);
    setFlyingKey(null);
    setDragX(0);
  };

  const startFly = (
    item: BannerItem,
    direction: 1 | -1,
    mode: "dismiss" | "browse",
    step: 1 | -1 = 1,
  ) => {
    setFlyMode(mode);
    setFlyDirection(direction);
    setFlyingKey(item.key);
    setIsDragging(false);
    window.setTimeout(
      () => (mode === "dismiss" ? finishDismiss(item) : finishBrowse(step)),
      FLY_OUT_MS,
    );
  };

  // Arrow navigation animates the same horizontal slide as a swipe (rather than
  // an instant restack): next (right) slides the top card out to the left,
  // previous (left) slides it out to the right.
  const browse = (step: 1 | -1) => {
    if (isFlying) return;
    startFly(topItem, step === 1 ? -1 : 1, "browse", step);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isFlying) return;
    pointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    draggedRef.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId || isFlying) return;
    const deltaX = e.clientX - startXRef.current;
    if (!draggedRef.current && Math.abs(deltaX) < DRAG_START_TOLERANCE) return;
    if (!draggedRef.current) {
      // Capture only once a real drag starts — capturing on pointer-down
      // retargets the eventual click at this div, which silently breaks the
      // banner's button.
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    draggedRef.current = true;
    setIsDragging(true);
    setDragX(deltaX);
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    pointerIdRef.current = null;
    if (isFlying) return;
    setIsDragging(false);
    if (Math.abs(dragX) >= SWIPE_THRESHOLD) {
      // A swipe browses to the next card; it must not dismiss it.
      startFly(topItem, dragX > 0 ? 1 : -1, "browse");
    } else {
      setDragX(0);
    }
  };

  // A drag that ends on the banner's button would otherwise register as a
  // click — swallow it so swiping never triggers navigation.
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const stack = orderedItems.slice(0, VISIBLE_STACK_DEPTH);

  return (
    <div className={classNames(styles.wrapper, "flex flex-col gap-2 w-full")}>
      <div className="flex items-center justify-between flex-wrap gap-1">
        <span className={styles.count}>
          Projects pending your review ({visibleItems.length})
        </span>
        <span className={styles.hint}>Swipe to browse · tap ✕ to dismiss</span>
      </div>

      <div className={styles.deck}>
        {stack.map((item, depth) => {
          const isTop = depth === 0;
          const style: React.CSSProperties = isTop
            ? {
                transform: isFlying
                  ? `translateX(${flyDirection * 120}%)`
                  : `translateX(${dragX}px)`,
                // Fade out only when dismissing; a browse swipe keeps the card
                // visible as it slides so it reads as moving to the back.
                opacity: isFlying && flyMode === "dismiss" ? 0 : 1,
                transition: isDragging
                  ? "none"
                  : `transform ${FLY_OUT_MS}ms ease, opacity ${FLY_OUT_MS}ms ease`,
                zIndex: VISIBLE_STACK_DEPTH,
              }
            : {
                transform: `translateY(${depth * 10}px) scale(${1 - depth * 0.04})`,
                zIndex: VISIBLE_STACK_DEPTH - depth,
              };
          return (
            <div
              key={item.key}
              className={classNames(styles.card, !isTop && styles.cardBehind)}
              style={style}
              {...(isTop
                ? {
                    onPointerDown: handlePointerDown,
                    onPointerMove: handlePointerMove,
                    onPointerUp: handlePointerEnd,
                    onPointerCancel: handlePointerEnd,
                    onClickCapture: handleClickCapture,
                  }
                : {})}
            >
              <ProjectNotificationBanner
                title={item.title}
                description={item.description}
                buttonText={item.buttonText}
                showButton={item.showButton}
                onButtonClick={item.onButtonClick}
                secondaryButtonText={item.secondaryButtonText}
                onSecondaryButtonClick={item.onSecondaryButtonClick}
                onDismissClick={
                  isTop && !isFlying
                    ? () => startFly(item, 1, "dismiss")
                    : undefined
                }
                animated={false}
              />
            </div>
          );
        })}

        {count > 1 && (
          <>
            <button
              type="button"
              className={classNames(styles.navArrow, styles.navArrowLeft)}
              onClick={() => browse(-1)}
              aria-label="Previous notification"
            >
              <Icons.ArrowLeft className={styles.navArrowIcon} />
            </button>
            <button
              type="button"
              className={classNames(styles.navArrow, styles.navArrowRight)}
              onClick={() => browse(1)}
              aria-label="Next notification"
            >
              <Icons.ArrowRight className={styles.navArrowIcon} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectNotificationBannerCarousel;
